import merge from 'deepmerge';
import React, { useContext, useMemo } from 'react';
import {
  ThemeProvider as StyledComponentsThemeProvider,
  useTheme as useStyledComponentsTheme,
} from 'styled-components';
import { DefaultLink } from './DefaultLink';
import { DEFAULT_THEME } from './Theme';
import { LinkProps, ThemeContext } from './ThemeContext';

interface ThemeContextType {
  LinkComponent: React.ComponentType<LinkProps>;
  isLinkActive: (path: string) => boolean;
  siteOrigin?: string;
}

interface ThemeProviderProps {
  link?: ThemeContextType['LinkComponent'];
  isLinkActive?: ThemeContextType['isLinkActive'];
  siteOrigin?: string;
  theme?: React.ComponentProps<typeof StyledComponentsThemeProvider>['theme'];
}

export const useTheme = () => {
  const theme = useContext(ThemeContext);

  return useMemo(
    () => ({
      ...theme,
      LinkComponent: theme.LinkComponent || DefaultLink,
    }),
    [theme]
  );
};

export const ThemeProvider: React.FC<
  React.PropsWithChildren<React.PropsWithChildren<ThemeProviderProps>>
> = ({ link, isLinkActive, siteOrigin, theme, children }) => {
  const parentContextValue = useTheme();
  const styledComponentsTheme = useStyledComponentsTheme();

  const contextValue = useMemo(() => {
    const newContext: Partial<ThemeContextType> = {};

    if (link) {
      newContext.LinkComponent = link;
    }

    if (isLinkActive) {
      newContext.isLinkActive = isLinkActive;
    }

    if (siteOrigin !== undefined) {
      newContext.siteOrigin = siteOrigin;
    }

    return merge(parentContextValue, newContext);
  }, [parentContextValue, link, isLinkActive, siteOrigin]);

  return (
    <ThemeContext.Provider value={contextValue}>
      <StyledComponentsThemeProvider theme={theme || styledComponentsTheme || DEFAULT_THEME}>
        {children}
      </StyledComponentsThemeProvider>
    </ThemeContext.Provider>
  );
};
